import { required, email, minLength, helpers } from '@vuelidate/validators'
import type { ValidationRule } from '@vuelidate/core'

/**
 * Centrální validační pravidla a schemas pro celou aplikaci
 * Zajišťuje konzistenci validačních zpráv a znovupoužitelnost pravidel
 *
 * ⚠️ SECURITY NOTE:
 * Frontend validation is NOT a security boundary - it only improves user experience.
 * All data MUST be validated on the backend using Symfony DTOs with Assert validators.
 * See: api/src/DTO/* for backend validation rules.
 *
 * Frontend validation can be bypassed by:
 * - Modifying browser console
 * - Disabling JavaScript
 * - Direct API calls
 *
 * Always validate on backend!
 */

// =============================================================================
// ZÁKLADNÍ VALIDÁTORY S ČESKÝMI ZPRÁVAMI
// =============================================================================

export const validationMessages = {
  required: 'Toto pole je povinné',
  email: 'Zadejte platný email',
  minLength: (min: number) => `Musí obsahovat alespoň ${min} znaků`,
  passwordMinLength: 'Heslo musí obsahovat minimálně 8 znaků',
  passwordsMatch: 'Hesla se neshodují',
} as const

/**
 * Základní validátory s předdefinovanými českými chybovými zprávami
 */
export const validators = {
  required: helpers.withMessage(validationMessages.required, required),
  email: helpers.withMessage(validationMessages.email, email),
  minLength: (length: number) =>
    helpers.withMessage(validationMessages.minLength(length), minLength(length)),
} as const

// =============================================================================
// CUSTOM VALIDÁTORY
// =============================================================================

/**
 * Validuje, že pole obsahuje alespoň jeden překlad
 */
export const hasAtLeastOneTranslation = helpers.withMessage(
  'Musí existovat alespoň jeden překlad',
  (value: Record<string, unknown>) => Object.keys(value).length > 0
)

/**
 * Validuje, že alespoň první (výchozí) překlad má vyplněný název/title.
 * Ostatní locale mohou mít prázdný title — nevyplněné překlady se přeskočí.
 */
export const eachTranslationHasTitle = helpers.withMessage(
  'Výchozí překlad musí mít vyplněný název',
  (value: Record<string, { title: string }>) => {
    const entries = Object.values(value)
    if (entries.length === 0) return false
    // First locale (default) must have a title
    return !!entries[0].title
  }
)

/**
 * Validuje shodu hesel
 */
export const createSameAsPasswordValidator = (passwordRef: { value: string }) =>
  helpers.withMessage(
    validationMessages.passwordsMatch,
    (value: string) => value === passwordRef.value
  )

/**
 * Validuje formát kódu projektu:
 * - Pouze malá písmena, číslice a pomlčky
 * - 3-50 znaků
 * - Nesmí začínat ani končit pomlčkou
 * - Nesmí obsahovat dvojité pomlčky
 */
export const projectCodeFormat = helpers.withMessage(
  'Kód projektu: 3-50 znaků, malá písmena, číslice, pomlčky (ne na začátku/konci, ne dvojité)',
  (value: string) => {
    if (!value || value.length < 3 || value.length > 50) return false
    // Pattern: starts with alphanumeric, optionally followed by groups of hyphen + alphanumeric
    return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)
  }
)

/**
 * Validuje URL adresy povolených domén
 */
export const validAllowedOrigins = helpers.withMessage(
  'Některé z povolených domén nemají platný formát (povolené formáty: test.cz, *.example.com, https://example.com, localhost, http://localhost:3000)',
  (value: string) => {
    if (!value.trim()) return true

    const urls = value
      .split('\n')
      .map(origin => origin.trim())
      .filter(origin => origin !== '')

    return urls.every(validateUrl)
  }
)

/**
 * Validuje locale (musí být v seznamu vybraných locales)
 */
export const createValidLocaleValidator = (localesRef: { value: string[] }) =>
  helpers.withMessage(
    'Výchozí jazyk musí být jeden z vybraných jazyků',
    (value: string) => localesRef.value.includes(value)
  )

/**
 * Validuje currency (musí být v seznamu dostupných měn)
 */
export const createValidCurrencyValidator = (currenciesRef: { value: string[] }) =>
  helpers.withMessage(
    'Výchozí měna musí být jedna z dostupných měn',
    (value: string) => currenciesRef.value.includes(value)
  )

/**
 * Validuje minimální délku pole
 */
export const createMinArrayLengthValidator = (min: number, message: string) =>
  helpers.withMessage(
    message,
    (value: unknown[]) => value.length >= min
  )

// =============================================================================
// HELPER FUNKCE
// =============================================================================

/**
 * Validuje jednotlivou URL adresu
 */
function validateUrl(url: string): boolean {
  const simpleDomainRegex = /^[a-zA-Z0-9][-a-zA-Z0-9]*(\.[a-zA-Z0-9][-a-zA-Z0-9]*)+$/
  const fullUrlRegex = /^https?:\/\/[-a-zA-Z0-9@:%._+~#=]{1,256}(\.[a-zA-Z0-9()]{1,6})*([-a-zA-Z0-9()@:%_+.~#?&/=]*)/
  const localhostWithProtocolRegex = /^https?:\/\/localhost(:[0-9]+)?(\/.*)?$/
  const localhostWithoutProtocolRegex = /^localhost(:[0-9]+)?(\/.*)?$/

  if (url.startsWith('*.')) {
    const domainPart = url.substring(2)
    return /^[a-zA-Z0-9][a-zA-Z0-9-]*(\.[a-zA-Z0-9-]+)+$/.test(domainPart)
  }

  return (
    simpleDomainRegex.test(url) ||
    fullUrlRegex.test(url) ||
    localhostWithProtocolRegex.test(url) ||
    localhostWithoutProtocolRegex.test(url)
  )
}

// =============================================================================
// SCHEMA FACTORIES - Předdefinované validační sady pro entity
// =============================================================================

/**
 * Validační schema pro kategorie
 */
export const categorySchema = () => ({
  type: {
    required: helpers.withMessage('Typ kategorie je povinný', required)
  },
  translations: {
    hasAtLeastOneTranslation,
    eachTranslationValid: eachTranslationHasTitle
  }
})

/**
 * Conditional validator - personalId required if isPartner is true
 */
const requiredIfPartner = (formData: { isPartner: boolean }) =>
  helpers.withMessage(
    'Partner ID je povinné pro partnery',
    (value: string) => {
      if (!formData.isPartner) return true // Not required if not a partner
      return !!value && value.trim() !== '' // Required if partner
    }
  )

/**
 * Validates personalId format (5-6 digit number)
 */
const personalIdFormat = helpers.withMessage(
  'Partner ID musí být 5 nebo 6 místné číslo',
  (value: string) => {
    if (!value || value.trim() === '') return true // Optional field, skip if empty
    return /^\d{5,6}$/.test(value)
  }
)

/**
 * Validační schema pro zákazníky - základní informace
 */
export const customerSchema = (formData?: { isPartner: boolean }) => ({
  email: {
    required: helpers.withMessage('Email je povinný', required),
    email: helpers.withMessage('Zadejte platný email', email)
  },
  name: {
    required: helpers.withMessage('Jméno je povinné', required)
  },
  personalId: formData ? {
    requiredIfPartner: requiredIfPartner(formData),
    personalIdFormat
  } : {}
})

/**
 * Validační schema pro heslo zákazníka
 */
export const customerPasswordSchema = (passwordRef: { value: string }) => ({
  password: {
    required: helpers.withMessage('Heslo je povinné', required),
    minLength: helpers.withMessage('Heslo musí mít alespoň 8 znaků', minLength(8))
  },
  confirmPassword: {
    required: helpers.withMessage('Potvrzení hesla je povinné', required),
    sameAsPassword: createSameAsPasswordValidator(passwordRef)
  }
})

/**
 * Validační schema pro uživatele
 */
export const userSchema = (isEditMode: boolean) => ({
  email: {
    required: helpers.withMessage('Email je povinný', required),
    email: helpers.withMessage('Zadejte platný email', email)
  },
  name: {
    required: helpers.withMessage('Jméno je povinné', required),
    minLength: helpers.withMessage('Jméno musí mít alespoň 2 znaky', minLength(2))
  },
  password: {
    required: helpers.withMessage('Heslo je povinné', !isEditMode ? required : () => true),
    minLength: helpers.withMessage(
      validationMessages.passwordMinLength,
      (value: string) => {
        if (isEditMode && !value) return true
        return !!value && value.length >= 8
      }
    )
  }
})

/**
 * Validační schema pro projekty
 */
export const projectSchema = (
  localesRef: { value: string[] },
  currenciesRef: { value: string[] }
) => ({
  name: {
    required: helpers.withMessage('Název projektu je povinný', required),
    minLength: helpers.withMessage('Název projektu musí mít alespoň 3 znaky', minLength(3))
  },
  code: {
    required: helpers.withMessage('Kód projektu je povinný', required),
    minLength: helpers.withMessage('Kód projektu musí mít alespoň 3 znaky', minLength(3)),
    validFormat: projectCodeFormat
  },
  locales: {
    required: helpers.withMessage('Je nutné vybrat alespoň jeden jazyk', required),
    minLength: createMinArrayLengthValidator(1, 'Je nutné vybrat alespoň jeden jazyk')
  },
  defaultLocale: {
    required: helpers.withMessage('Je nutné vybrat výchozí jazyk', required),
    validLocale: createValidLocaleValidator(localesRef)
  },
  defaultCurrency: {
    required: helpers.withMessage('Je nutné vybrat výchozí měnu', required),
    validCurrency: createValidCurrencyValidator(currenciesRef)
  }
})

/**
 * Validační schema pro notifikace
 */
export const notificationSchema = () => ({
  recipient: {
    required: helpers.withMessage('Email příjemce je povinný', required),
    email: helpers.withMessage('Zadejte platný email', email)
  },
  subject: {
    required: helpers.withMessage('Předmět je povinný', required)
  },
  message: {
    required: helpers.withMessage('Zpráva je povinná', required)
  }
})

/**
 * Validační schema pro produkty
 */
export const productSchema = () => ({
  type: {
    required: helpers.withMessage('Typ produktu je povinný', required)
  },
  category: {
    required: helpers.withMessage('Kategorie je povinná', required)
  },
  translations: {
    hasAtLeastOneTranslation,
    eachTranslationValid: eachTranslationHasTitle
  }
})

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type ValidationSchema = Record<string, Record<string, ValidationRule>>
